// balanceAsOf.js — "As of" label for a stored bank balance (2026-10-28).
//
// Plaid balances are a SNAPSHOT: /transactions/sync hands back account
// balances a few times a day and api/plaid.js stamps plaid_cache.
// balances_updated_at when they land (the same column Kai and the cron read
// through the 12h freshness window). The app shows that time next to every
// balance so a number is never mistaken for a live figure:
//   today            "As of 2:14 PM"
//   yesterday        "As of yesterday 9:02 PM"
//   older            "As of Oct 26"   (with the year when it differs)
//   unknown/invalid  null             (show nothing, never a fake time)
// Dates are judged in the user's local calendar (or `zone` when given), so a
// snapshot from 11:59 PM reads "yesterday" at 12:01 AM. Pure, no React.

export const BALANCE_SNAPSHOT_TOAST = 'Balances update from your bank a few times a day.';

function partsIn(ms, zone) {
  const f = new Intl.DateTimeFormat('en-US', {
    ...(zone ? { timeZone: zone } : {}),
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
  const p = {};
  for (const x of f.formatToParts(new Date(ms))) if (x.type !== 'literal') p[x.type] = x.value;
  return p;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayKey = (p) => `${p.year}-${MONTHS.indexOf(p.month)}-${p.day}`;

/**
 * The label for a balance stamped at `iso` (ISO string or epoch ms), judged
 * against `now` (epoch ms, default Date.now()). `zone` is an IANA zone for
 * tests; omitted, the device's local zone is used.
 */
export function balanceAsOfLabel(iso, { now = Date.now(), zone } = {}) {
  if (iso == null || iso === '') return null;
  const ms = typeof iso === 'number' ? iso : Date.parse(String(iso));
  if (!Number.isFinite(ms)) return null;
  const then = partsIn(ms, zone);
  const today = partsIn(now, zone);
  // Yesterday's calendar date, built from today's Y/M/D so a DST change never
  // shifts it: a day in UTC arithmetic is always 24h.
  const yesterday = partsIn(Date.UTC(Number(today.year), MONTHS.indexOf(today.month), Number(today.day) - 1, 12), 'UTC');
  const time = `${then.hour}:${then.minute} ${then.dayPeriod}`;
  if (dayKey(then) === dayKey(today)) return `As of ${time}`;
  if (dayKey(then) === dayKey(yesterday)) return `As of yesterday ${time}`;
  return `As of ${then.month} ${then.day}${then.year !== today.year ? `, ${then.year}` : ''}`;
}
